<template>
  <v-card
    id="feed-card"
    class="max-shadow"
  >
    <v-card-title class="py-4">
      <div class="title-2">
        <v-icon
          v-if="step === 2"
          class="pr-2"
          color="primary"
          @click="goback"
        >
          arrow_back
        </v-icon>
        Feed
      </div>
      <v-spacer />
    </v-card-title>
    <v-divider />
    <v-layout
      v-if="feeds.length === 0"
      row
      wrap
      align-center
      justify-center
      class="py-1"
    >
      <span class="body-2 grey--text font-weight-medium">
        There's no feed to show
      </span>
    </v-layout>
    <div class="feed-list px-1">
      <v-window
        v-model="step"
        mandatory
      >
        <v-window-item
          :value="1"
        >
          <v-list>
            <!-- show-comments : If true, show comments in each type of feed
            we might do it in child component. But the back button is in this component.
            So we should tell the child component to show or not to show the comments from this component -->
            
            <!-- @show-comments : event gets emitted from a type of feed when we click on comment icon -->
            <template v-for="feed in feeds">
              <post
                v-if="feed.type === 'post'"
                :key="feed.id"
                :post="feed"
                class="my-2"
                :show-comments="show && selectedFeed === null"
                @show-comments="showFeed" 
                @delete="deleteFeed"
              />
              <poll
                v-else-if="feed.type === 'poll'"
                :key="feed.id"
                :poll="feed"
                class="my-2"
                :show-comments="show && selectedFeed === null"
                @show-comments="showFeed"
                @delete="deleteFeed"
              />
              <event
                v-else
                :key="feed.id"
                :event="feed"
                class="my-2"
                :show-comments="show && selectedFeed === null"
                @show-comments="showFeed"
                @delete="deleteFeed"
              />
            </template>
            <v-layout
              row
              wrap
              justify-center
            >
              <v-btn
                v-if="feeds.length !== 0 && feeds.length % 15 === 0"
                color="primary"
                flat
                center
                :loading="loadMoreLoading"
                @click="loadMoreFeeds"
              >
                load more
              </v-btn>
            </v-layout>
          </v-list>
        </v-window-item>
        <v-window-item
          lazy
          :value="2"
        >
          <v-list>
            <post
              v-if="selectedFeed && selectedFeed.type === 'post'"
              :post="selectedFeed"
              :show-comments="show"
              class="my-2"
              @delete="deleteFeed"
            />
            <poll
              v-else-if="selectedFeed && selectedFeed.type === 'poll'"
              :poll="selectedFeed"
              :show-comments="show"
              class="my-2"
              @delete="deleteFeed"
            />
            <event
              v-else
              :event="selectedFeed"
              :show-comments="show"
              class="my-2"
              @delete="deleteFeed"
            />
          </v-list>
        </v-window-item>
      </v-window>
    </div>
    <v-dialog
      v-model="deleteDialog"
      persistent
      max-width="700px"
      transition="dialog-transition"
      lazy
    >
      <v-card>
        <v-card-title class="headline">
          Delete {{ selectedFeed.author.name }}'s {{ selectedFeed.type }}?
        </v-card-title>
        <v-card-text class="subheading">
          Do you want to delete {{ selectedFeed.author.name }}'s {{ selectedFeed.type }}?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-spacer />
          <v-btn
            color="primary"
            flat
            @click="deleteDialog = false"
          >
            cancel
          </v-btn>
          <v-btn
            flat
            color="primary"
            @click="confirmDeleteFeed"
          >
            delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      v-model="snackbar"
      :color="snackColor"
      :timeout="0"
      bottom
    >
      {{ snackbarMessage }}
      <v-btn
        flat
        @click.native="snackbar = false"
      >
        Close
      </v-btn>
    </v-snackbar>
  </v-card>
</template>
<script>
import { firestore } from '@/firebase';
import Post from './Post';
import Poll from './Poll';
import Event from './Event';

export default {
  name: 'Feed',
  components: {
    post: Post,
    poll: Poll,
    event: Event
  },
  data() {
    return {
      feeds: [],
      step: 1,
      loadMoreLoading: false,
      selectedFeed: {
        author: {}
      },
      deleteDialog: false,
      show: false,
      snackbar: false,
      snackColor: 'success',
      snackbarMessage: ''
    };
  },
  computed: {
    role() {
      return this.$store.state.profile.role;
    },
    cid() {
      return this.$store.state.profile.cid;
    },
    feedRef() {
      return this.role === 'admin'
        ? firestore
            .collection('feed')
            .orderBy('createdAt', 'desc')
            .where('author.cid', '==', this.cid)
            .where('private', '==', true)
        : firestore
            .collection('feed')
            .where('private', '==', false)
            .orderBy('createdAt', 'desc');
    }
  },
  mounted() {
    this.feedRef
      .limit(15)
      .get()
      .then(feed => {
        const docs = [];
        feed.forEach(feedSnapshot => {
          docs.push({ ...feedSnapshot.data(), ...{ id: feedSnapshot.id } });
        });
        this.feeds = docs;
      });
  },
  methods: {
    loadMoreFeeds() {
      const lastFeed = this.feeds[this.feeds.length - 1];
      this.loadMoreLoading = true;
      const query = this.feedRef.startAfter(lastFeed.createdAt).limit(15);
      query.get().then(feed => {
        feed.forEach(feedSnapshot => {
          this.feeds.push({
            ...feedSnapshot.data(),
            ...{ id: feedSnapshot.id }
          });
        });
        this.loadMoreLoading = false;
      });
    },
    showFeed(post) {
      this.selectedFeed = post;
      this.step++;
      this.show = true;
    },
    deleteFeed(feed) {
      this.selectedFeed = feed;
      this.deleteDialog = true;
    },
    confirmDeleteFeed() {
      firestore
        .collection('feed')
        .doc(this.selectedFeed.id)
        .delete()
        .then(() => {
          this.snackColor = 'primary';
          this.snackbarMessage = 'Deleted successfully';
          this.snackbar = true;
          this.feeds = this.feeds.filter(
            feed => feed.id !== this.selectedFeed.id
          );
          this.step--;
          this.deleteDialog = false;
        })
        .catch(() => {
          this.snackColor = 'error';
          this.snackbarMessage =
            'Something went wrong. Please try after sometime';
          this.snackbar = true;
          this.deleteDialog = false;
        });
    },
    goback() {
      this.step--;
      this.show = false;
    }
  }
};
</script>
<style>
#feed-card {
  height: calc(100vh - 158px);
}
.feed-list {
  height: calc(100vh - 230px);
  overflow-y: scroll;
}
.feed-image {
  border-radius: 6px;
  margin-top: 8px;
}
</style>

